import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { useState } from 'react';

const DashboardMenu = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'favorites'>('all');

  const [favorites, setFavorites] = useState<number[]>(() => {
    try {
      const saved = localStorage.getItem('dashboard_favorites');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [hiddenItems, setHiddenItems] = useState<number[]>(() => {
    try {
      const saved = localStorage.getItem('dashboard_hidden');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const allMenuItems = [
    { id: 1, title: 'Đơn hàng mua trong nước', path: '/mua-hang/don-dat-hang/don-hang-mua-trong-nuoc' },
    { id: 2, title: 'Vật tư, sản phẩm', path: '/danh-muc/doi-tuong/vat-tu-san-pham' },
    { id: 3, title: 'Phiếu xuất điều chuyển', path: '/ton-kho/phieu-xuat-dieu-chuyen' },
    { id: 4, title: 'Khách hàng', path: '/danh-muc/doi-tuong/khach-hang' },
    { id: 5, title: 'Phiếu yêu cầu xuất kho', path: '/ton-kho/phieu-yeu-cau-xuat-kho' },
    { id: 6, title: 'Hóa đơn bán hàng', path: '/ban-hang/hoa-don-ban-ra/hoa-don-ban-hang' },
    { id: 7, title: 'Hóa đơn mua hàng trong nước', path: '/mua-hang/hoa-don-mua-vao/hoa-don-mua-hang-trong-nuoc' },
    { id: 8, title: 'Hóa đơn nhập mua - xuất thẳng', path: '/phai-tra/hoa-don-nhap-mua-xuat-thang' },
    { id: 9, title: 'Hóa đơn mua dịch vụ', path: '/mua-hang/hoa-don-mua-vao/hoa-don-mua-dich-vu' },
    { id: 10, title: 'Hóa đơn bán dịch vụ', path: '/ban-hang/hoa-don-ban-ra/hoa-don-ban-dich-vu' },
    { id: 11, title: 'Kho hàng', path: '/danh-muc/vat-tu-san-pham-va-kho/kho-hang' },
    { id: 12, title: 'Phiếu yêu cầu kiểm kê', path: '/ton-kho/kiem-ke/phieu-yeu-cau-kiem-ke' },
    { id: 13, title: 'Phí', path: '/danh-muc/ke-toan/phi' },
    { id: 14, title: 'Giá mua', path: '/danh-muc/mua-hang/gia-mua' },
    { id: 15, title: 'Giá bán', path: '/danh-muc/ban-hang/gia-ban' },
    { id: 16, title: 'Phiếu nhập kho', path: '/ton-kho/phieu-nhap-kho' },
    { id: 17, title: 'Phiếu xuất kho', path: '/ton-kho/phieu-xuat-kho' },
    { id: 18, title: 'Khai báo giảm TSCĐ', path: '/tai-san/khai-bao-giam-tscd' },
    {
      id: 19,
      title: 'Khai báo thông tin tài sản cố định',
      path: '/tai-san/khai-bao-tang-giam-tscd/khai-bao-thong-tin-tscd'
    },
    { id: 20, title: 'Địa chỉ nhận hàng', path: '/danh-muc/mua-hang/dia-chi-nhan-hang' }
  ];

  const toggleFavorite = (id: number) => {
    const nextFavorites = favorites.includes(id) ? favorites.filter(favId => favId !== id) : [...favorites, id];
    setFavorites(nextFavorites);
    localStorage.setItem('dashboard_favorites', JSON.stringify(nextFavorites));
  };

  const hideItem = (id: number) => {
    const nextHidden = [...hiddenItems, id];
    setHiddenItems(nextHidden);
    localStorage.setItem('dashboard_hidden', JSON.stringify(nextHidden));
  };

  const resetHiddenItems = () => {
    setHiddenItems([]);
    localStorage.removeItem('dashboard_hidden');
  };

  const handleCardClick = (item: (typeof allMenuItems)[0]) => {
    if (item.title && item.path) {
      navigate(item.path);
    }
  };

  // 1. Lọc theo danh sách ẩn
  const visibleItems = allMenuItems.filter(item => !hiddenItems.includes(item.id));

  // 2. Lọc theo tab Yêu thích
  const tabFilteredItems =
    activeTab === 'favorites' ? visibleItems.filter(item => favorites.includes(item.id)) : visibleItems;

  // 3. Lọc danh sách theo từ khóa tìm kiếm
  const filteredMenuItems = tabFilteredItems.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Đảm bảo luôn có đủ 20 ô
  const paddedMenuItems = [...filteredMenuItems];
  let nextId = allMenuItems.length + 1;
  while (paddedMenuItems.length < 20) {
    paddedMenuItems.push({ id: nextId++, title: '', path: '' });
  }

  return (
    <div className='mx-auto w-full max-w-[1500px] border-0 p-3 lg:border lg:border-dotted'>
      <div className='mx-auto h-auto'>
        {/* Controls bar */}
        <div className='mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          {/* Search box */}
          <div className='relative flex-1'>
            <input
              type='text'
              placeholder='Tìm kiếm chức năng...'
              className='w-full rounded border-[2px] border-gray-400 bg-white px-3 py-2 text-sm hover:border-[#023e8a] hover:ring-1 hover:ring-[#023e8a] focus:border-[#023e8a] focus:outline-none focus:ring-1 focus:ring-[#023e8a]'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
              >
                <Lineicons aria-hidden='true' icon={XmarkStroke} className='h-4 w-4' />
              </button>
            )}
          </div>

          {/* Tabs & Actions */}
          <div className='flex flex-wrap items-center gap-2'>
            <div className='inline-flex rounded-md border border-gray-300 bg-gray-50 p-1'>
              <button
                onClick={() => setActiveTab('all')}
                className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeTab === 'all' ? 'bg-white text-[#023e8a] shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Tất cả
              </button>
              <button
                onClick={() => setActiveTab('favorites')}
                className={`flex items-center gap-1 rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeTab === 'favorites' ? 'bg-white text-[#023e8a] shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yêu thích{' '}
                <Star
                  className={`h-3 w-3 ${favorites.length > 0 ? 'fill-yellow-400 text-yellow-500' : 'text-gray-400'}`}
                />
              </button>
            </div>

            {hiddenItems.length > 0 && (
              <button
                onClick={resetHiddenItems}
                className='rounded border border-dashed border-[#023e8a] px-3 py-1.5 text-xs font-medium text-[#023e8a] transition-colors hover:bg-blue-50'
              >
                Hiển thị lại {hiddenItems.length} mục đã ẩn
              </button>
            )}
          </div>
        </div>

        {/* Menu grid */}
        <div className='grid h-auto grid-cols-1 gap-4 pb-14 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 lg:pb-5 xl:grid-cols-5'>
          {paddedMenuItems.map(item => (
            <div
              key={item.id}
              onClick={() => handleCardClick(item)}
              className={`group relative flex h-[120px] flex-col rounded-sm border-[2px] transition ${
                item.title
                  ? 'cursor-pointer border-gray-400 hover:border-[#023e8a] hover:ring-1 hover:ring-[#023e8a]'
                  : 'hidden cursor-default border-transparent lg:block'
              }`}
            >
              <span
                className={`flex h-1/3 items-center border-b border-gray-300 py-1 pl-2 pr-1 text-left text-xs ${
                  item.title ? 'border-gray-300' : 'border-transparent'
                }`}
              >
                {item.title}
              </span>
              <div className='h-full w-full'>
                {item.title && (
                  <>
                    {/* Floating favorited star when not hovered */}
                    <div
                      className={`absolute bottom-2 right-2 transition-opacity duration-200 group-hover:pointer-events-none group-hover:opacity-0 ${
                        favorites.includes(item.id) ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <Star className='h-5 w-5 fill-yellow-400 text-yellow-500' />
                    </div>

                    {/* Actions box showing on hover */}
                    <div className='absolute bottom-1 right-1 flex bg-white opacity-100 transition-opacity duration-200 sm:opacity-0 sm:group-hover:opacity-100'>
                      <button
                        type='button'
                        onClick={e => {
                          e.stopPropagation();
                          toggleFavorite(item.id);
                        }}
                        className='flex h-7 w-8 items-center justify-center border-[0.8px] border-r-0 border-gray-300 bg-white p-0 text-gray-400 outline-none hover:text-yellow-400'
                        title={favorites.includes(item.id) ? 'Bỏ yêu thích' : 'Yêu thích'}
                      >
                        <Star
                          className={`h-5 w-5 ${favorites.includes(item.id) ? 'fill-yellow-400 text-yellow-500' : ''}`}
                        />
                      </button>
                      <button
                        type='button'
                        onClick={e => {
                          e.stopPropagation();
                          hideItem(item.id);
                        }}
                        className='flex h-7 w-8 items-center justify-center border-[0.8px] border-gray-300 bg-white p-0 text-gray-400 outline-none hover:text-red-400'
                        title='Ẩn chức năng này'
                      >
                        <Lineicons aria-hidden='true' icon={XmarkStroke} className='h-5 w-5' />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardMenu;
